/**
 * Return URL Storage Helpers
 *
 * Manages storing and retrieving the return URL for post-auth redirects.
 * Uses sessionStorage to persist across page navigation but not across sessions.
 */

const RETURN_URL_KEY = 'raflr_return_url';

/**
 * Allowed path prefixes for return URLs.
 * Prevents open redirect vulnerabilities by validating paths.
 */
const ALLOWED_PREFIXES = [
  '/',
  '/competition/',
  '/browse',
  '/create',
  '/my-raffles',
  '/settings',
  '/messages',
];

/**
 * Validates a return URL to prevent open redirect attacks.
 * Only allows relative paths starting with allowed prefixes.
 */
function isValidReturnUrl(url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Must be a relative path (no protocol/host)
  if (url.includes('://') || url.startsWith('//')) {
    return false;
  }

  // Must start with /
  if (!url.startsWith('/')) {
    return false;
  }

  // Check against allowed prefixes
  return ALLOWED_PREFIXES.some(prefix => url.startsWith(prefix));
}

/**
 * Get the stored return URL.
 * Returns null if none stored or if invalid.
 */
export function getReturnUrl() {
  try {
    const url = sessionStorage.getItem(RETURN_URL_KEY);
    if (url && isValidReturnUrl(url)) {
      return url;
    }
  } catch (e) {
    // sessionStorage may not be available (SSR, private mode)
    console.warn('sessionStorage not available');
  }
  return null;
}

/**
 * Store a return URL for after authentication.
 * Only stores if the URL passes validation.
 */
export function setReturnUrl(url) {
  try {
    if (isValidReturnUrl(url)) {
      sessionStorage.setItem(RETURN_URL_KEY, url);
      return true;
    }
  } catch (e) {
    console.warn('sessionStorage not available');
  }
  return false;
}

/**
 * Clear the stored return URL.
 * Call this after successfully redirecting.
 */
export function clearReturnUrl() {
  try {
    sessionStorage.removeItem(RETURN_URL_KEY);
  } catch (e) {
    console.warn('sessionStorage not available');
  }
}

/**
 * Get the return URL and clear it in one operation.
 * Useful for redirect-then-clear pattern.
 */
export function popReturnUrl(defaultUrl = '/') {
  const url = getReturnUrl();
  clearReturnUrl();
  return url || defaultUrl;
}
